import { cn } from './cn';

/*
  Skeleton — заглушка загрузки, которая знает форму будущих данных (DESIGN.md R31).
  Приём характера: пока данные едут, на их месте стоит не серый прямоугольник вообще,
  а та же сетка, те же колонки и то же число строк. Когда данные приходят, ничего не прыгает:
  взгляд уже стоит там, где появится нужное число.

  Блеск идёт слева направо (animate-shimmer) и останавливается при reduced-motion.
  still — без блеска: ожидание остановилось (сбой загрузки рядом, R42), заглушка замерла.
*/

export function Skeleton({ className, still = false }: { className?: string; still?: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        'block h-3 rounded-xs bg-muted',
        !still && 'animate-shimmer skeleton-sheen motion-reduce:animate-none',
        className,
      )}
    />
  );
}

export interface SkeletonColumn {
  /** Ширина колонки в утилитах шкалы: w-24, w-16 … */
  width?: string;
  align?: 'left' | 'right';
}

/** Высота строки — как у настоящей таблицы: sm h-8 · md h-10 · lg h-12. */
const ROW_HEIGHT = { sm: 'h-8', md: 'h-10', lg: 'h-12' } as const;

interface TableSkeletonProps {
  columns: SkeletonColumn[];
  rows?: number;
  /** Плотные строки (h-8) — если высота строки не задана явно. */
  dense?: boolean;
  /** Высота строки настоящей таблицы; по умолчанию md, при dense — sm. */
  rowHeight?: keyof typeof ROW_HEIGHT;
  /** Показывать полосу шапки. */
  header?: boolean;
  /** Без блеска — ожидание остановилось. */
  still?: boolean;
  className?: string;
}

export function TableSkeleton({
  columns,
  rows = 5,
  dense,
  rowHeight,
  header = true,
  still,
  className,
}: TableSkeletonProps) {
  const row = ROW_HEIGHT[rowHeight ?? (dense ? 'sm' : 'md')];
  return (
    <div data-slot="table-skeleton" className={cn('w-full', className)} aria-busy="true">
      {header && (
        <div className={cn('flex items-center gap-4 border-b border-line', dense ? 'h-8' : 'h-10')}>
          {columns.map((c, i) => (
            <div key={i} className={cn('flex-1', c.align === 'right' && 'flex justify-end')}>
              <Skeleton still={still} className={cn('h-2 opacity-70', c.width ?? 'w-16')} />
            </div>
          ))}
        </div>
      )}
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className={cn('flex items-center gap-4 border-b border-line', row)}>
          {columns.map((c, i) => (
            <div key={i} className={cn('flex-1', c.align === 'right' && 'flex justify-end')}>
              <Skeleton still={still} className={c.width ?? 'w-24'} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
